import { TIconImage, TLogoImage } from '../styles/images'
import { RecurrenceRate } from './enums'

export type EmptyString = ''

export type TTaskSourceName = 'General Task' | 'Google Calendar'

export interface TTaskSourceV4 {
    name: TTaskSourceName
    logo: TLogoImage
}
export interface TConferenceCall {
    platform: string
    logo: TLogoImage | EmptyString
    url: string
}

export interface TSourcesResult {
    name: string
    authorization_url: string
}

export interface TTaskV4 {
    id: string
    optimisticId?: string // Used only internally, not sent in response
    id_ordering: number
    title: string
    deeplink: string
    body: string
    priority_normalized: number
    due_date: string
    source: TTaskSourceV4
    sender: string
    is_done: boolean
    is_deleted: boolean
    created_at: string
    updated_at: string
    deleted_at: string
    completed_at: string
    id_folder?: string
    id_nux_number?: number
    id_parent?: string
    subtask_ids?: string[]
    meeting_preparation_params?: TMeetingPreparationParams
    recurring_task_template_id?: string
    research?: TTaskResearch
    recurrence_suggestion?: TTaskRecurrenceSuggestion
}

export type TTaskRecurrenceSuggestionStatus =
    | 'pending'
    | 'evaluating'
    | 'ineligible'
    | 'ready'
    | 'dismissed'
    | 'accepted'

export interface TTaskRecurrenceSuggestion {
    status: TTaskRecurrenceSuggestionStatus
    recurrence_rate?: RecurrenceRate
    suggested_date?: string
}

export type TTaskResearchStatus = 'starting' | 'queued' | 'in_progress' | 'completed' | 'failed'

const TASK_RESEARCH_ACTIVE_STATUSES: readonly TTaskResearchStatus[] = ['starting', 'queued', 'in_progress']

export const isTaskResearchActive = (research?: Pick<TTaskResearch, 'status'> | null) =>
    !!research && TASK_RESEARCH_ACTIVE_STATUSES.includes(research.status)

export interface TTaskResearchSource {
    title: string
    url: string
}

export interface TTaskResearch {
    id: string
    question: string
    status: TTaskResearchStatus
    answer?: string
    sources?: TTaskResearchSource[]
    error_code?: string
    created_at: string
    updated_at: string
    completed_at?: string
    retained?: {
        question: string
        answer: string
        sources: TTaskResearchSource[]
        completed_at: string
    }
}

export interface TMeetingPreparationParams {
    datetime_start: string
    datetime_end: string
    event_moved_or_deleted: boolean
}

export interface TEvent {
    id: string
    optimisticId?: string
    title: string
    body: string
    account_id: string
    calendar_id: string
    color_id: string
    color_background?: string
    color_foreground?: string
    logo: TLogoImage
    deeplink: string
    datetime_start: string
    datetime_end: string
    can_modify: boolean
    conference_call: TConferenceCall
    linked_task_id: string
    linked_view_id: string
    linked_note_id?: string
    is_calendar_plan_suggestion?: boolean
    calendar_plan_reason?: string
}

export interface TTaskFolder {
    id: string
    name: string
    task_ids: string[]
    is_done: boolean
    is_trash: boolean
    optimisticId?: string
}

export interface TSettingChoice {
    choice_key: string
    choice_name: string
}

export type TSortAndFilterSettingRole = 'sort' | 'direction' | 'filter'

export interface TSortAndFilterSettingContract {
    group: string
    role: TSortAndFilterSettingRole
    scope_id?: string
    context?: string
}

export interface TSetting {
    field_key: string
    field_value: string
    field_name: string
    choices: TSettingChoice[]
    sort_and_filter?: TSortAndFilterSettingContract
}

export interface TSupportedType {
    name: string
    logo: string
    logo_v2: TLogoImage
    authorization_url: string
}
export interface TLinkedAccount {
    id: string
    display_id: string
    name: string
    logo: string
    logo_v2: TLogoImage
    is_unlinkable: boolean
    has_bad_token: boolean
}

export interface TUserInfo {
    agreed_to_terms: boolean
    opted_into_marketing: boolean
    name: string
    is_employee: boolean
    email: string
    subscription_status?: string
    is_subscribed?: boolean
    is_in_trial?: boolean
    trial_days_remaining?: number
    has_product_access?: boolean
    is_read_only?: boolean
    trial_ends_at?: string | null
}

export type TFeatureFlagKey =
    | 'routines'
    | 'goals'
    | 'research'
    | 'recurrence_suggestions'
    | 'due_today_email'
    | 'calendar_planning'

export interface TFeatureFlagOption {
    key: TFeatureFlagKey
    name: string
    description: string
    // Effective for this user: on globally, on for internal dashboard users, or opted in.
    enabled: boolean
    globally_enabled: boolean
    opted_in: boolean
}

// One effective boolean per flag, plus the flag list Settings renders.
export type TFeatureFlags = Record<TFeatureFlagKey, boolean> & {
    flags: TFeatureFlagOption[]
}

export interface TInternalFeatureFlag {
    key: TFeatureFlagKey
    name: string
    description: string
    enabled: boolean
    updated_at: string
    opted_in_count: number
    enabled_user_count: number
}

export type TInternalFeatureFlags = Record<TFeatureFlagKey, TInternalFeatureFlag>

export interface TInternalEmailTest {
    key: string
    name: string
    description: string
}

export type TEmailDeliveryConfigIssue = '' | 'key_missing' | 'placeholder_key' | 'not_prod'

export interface TEmailDeliveryStatus {
    enabled: boolean
    switched_on: boolean
    switch_updated_at: string
    config_issue: TEmailDeliveryConfigIssue
}

export interface TInternalEmailTests {
    delivery: TEmailDeliveryStatus
    emails: TInternalEmailTest[]
}

export interface TInternalEmailTestResult {
    key: string
    sent: boolean
    error?: string
}

export interface TInternalDashboardCounts {
    total_users: number
    signups_last_7_days: number
    signups_last_30_days: number
    activated_users: number
    activated_last_7_days: number
    paid_users: number
    trialing_users: number
}

export interface TInternalDashboardUser {
    email: string
    name: string
    created_at: string
    trial_ends_at: string
    subscription_status: string
    is_subscribed: boolean
    is_in_trial: boolean
    trial_days_remaining: number
    has_product_access: boolean
    task_count: number
    completed_task_count: number
    last_completed_at: string
    activated: boolean
    email_unsubscribed: boolean
    welcome_email_sent_at: string
    subscription_acknowledgment: '' | 'sent' | 'pending'
}

export interface TInternalDashboardSummary {
    snapshot_at: string
    counts: TInternalDashboardCounts
    recent_users: TInternalDashboardUser[]
}

export type TInternalUsageWindow = '7d' | '30d' | '90d'

export interface TInternalUsageGoals {
    users: number
    total: number
    created_in_window: number
    active: number
    completed: number
    archived: number
    active_with_linked_tasks: number
    median_linked_tasks_per_goal: number
}

export interface TInternalUsageRoutines {
    users: number
    total: number
    created_in_window: number
    days_completed: number
    without_completion_in_window: number
}

export interface TInternalUsageUser {
    email: string
    name: string
    subscription_status: string
    is_in_trial: boolean
    trial_days_remaining: number
    trial_ends_at: string
    goal_count: number
    routine_count: number
    last_activity_at: string
}

export interface TInternalUsage {
    snapshot_at: string
    window: TInternalUsageWindow
    window_start: string
    goals: TInternalUsageGoals
    routines: TInternalUsageRoutines
    users: TInternalUsageUser[]
}

// React-DND Item Types
export enum DropType {
    TASK = 'task',
    SUBTASK = 'subtask',
    NON_REORDERABLE_TASK = 'non-reorderable-task',
    DUE_TASK = 'due-task',
    WEEK_TASK_TO_CALENDAR_TASK = 'week-task-to-calendar-task',
    EVENT = 'event',
    EVENT_RESIZE_HANDLE = 'event-resize-handle',
    OVERVIEW_VIEW = 'overview-view',
    FOLDER = 'folder',
    OVERVIEW_VIEW_HEADER = 'overview-view-header',
}

export interface DropItem {
    id: string
    sectionId?: string
    task?: TTaskV4
    event?: TEvent
    folder?: TTaskFolder
    view?: TOverviewView
}

export type TRoutineStatus = 'current' | 'up_next' | 'complete' | 'empty'

export interface TRoutineItem {
    id: string
    optimisticId?: string
    title: string
    id_ordering: number
    is_completed: boolean
}

export interface TRoutine {
    id: string
    optimisticId?: string
    name: string
    id_ordering: number
    status: TRoutineStatus
    remaining_count: number
    streak: number
    items: TRoutineItem[]
}

export type TOverviewItem = TTaskV4 | TRoutineItem

export type TOverviewViewType = 'task_section' | 'meeting_preparation' | 'due_today' | 'routine'

export interface TOverviewView {
    id: string
    optimisticId?: string
    name: string
    type: TOverviewViewType
    task_section_id?: string
    routine_id?: string
    is_reorderable: boolean
    logo: TLogoImage
    view_items: TOverviewItem[]
    view_item_ids: string[]
    total_view_items?: number // the total number of items in the view without filters applied
    sources: TSourcesResult[]
    is_linked: boolean
    has_tasks_completed_today: boolean
    status?: TRoutineStatus
    remaining_count?: number
    streak?: number
}

export interface TSupportedViewItem {
    view_id: string // id of view if is_linked is true
    optimisticId?: string
    name: string
    is_linked: boolean
    task_section_id: string
    routine_id?: string
    logo: TLogoImage
    is_added: boolean
}

export interface TSupportedView {
    optimisticId?: string
    type: TOverviewViewType
    name: string
    logo: TLogoImage
    is_nested: boolean
    is_linked: boolean
    views: TSupportedViewItem[]
    authorization_url: string
}

export type TShortcutCategory = 'Tasks' | 'Calendar' | 'General' | 'Navigation'

export interface TShortcut {
    label: string
    key: string
    keyLabel: string
    category: TShortcutCategory
    icon?: TIconImage
    hideFromCommandPalette?: boolean
    action: () => void
}

export interface TRecurringTaskTemplate {
    id: string
    optimisticId?: string
    title: string
    body?: string
    id_task_section: string
    priority_normalized?: number
    recurrence_rate: RecurrenceRate
    time_of_day_seconds_to_create_task: number
    /**
     * Minutes of time one occurrence holds. The backend copies it onto every generated task's
     * `time_allocated`, which is the only value an hours goal is credited from — so 0 or absent
     * means this repeat moves an hours goal by nothing.
     */
    block_duration_minutes?: number
    day_to_create_task?: number
    month_to_create_task?: number
    last_backfill_datetime: string
    created_at: string
    updated_at: string
    is_deleted: boolean
    is_enabled: boolean
}

export type TLinkedAccountName = 'Google Calendar'

export interface TNote {
    id: string
    linked_event_id?: string
    linked_event_start?: string
    linked_event_end?: string
    title: string
    body: string
    author: string
    created_at: string
    updated_at: string
    is_deleted: boolean
    optimisticId?: string
}

export interface TCalendar {
    calendar_id: string
    color_id: string
    title: string
    can_write: boolean
    access_role: string
    color_background: string
    color_foreground: string
}

export interface TCalendarAccount {
    account_id: string
    calendars: TCalendar[]
    has_multical_scopes: boolean
    has_primary_calendar_scopes: boolean
}

export type TParentTask = TTaskV4 & Required<Pick<TTaskV4, 'id_folder'>>

export type TSubtask = TTaskV4 & Required<Pick<TTaskV4, 'id_parent'>>
